import { Link } from "react-router-dom";
import { useSEO } from "../lib/seo";
import { Reveal } from "../lib/reveal";
import { IMAGES, COMPANY } from "../data/content";

const PRINCIPLES = [
  {
    num: "01",
    title: "Maîtrise technique",
    text: "Chaque projet est étudié en tenant compte des contraintes du bâtiment et des solutions les plus adaptées.",
  },
  {
    num: "02",
    title: "Coordination",
    text: "Nous organisons et coordonnons les différents corps de métier afin d'assurer la cohérence globale du chantier.",
  },
  {
    num: "03",
    title: "Qualité d'exécution",
    text: "Une attention particulière est portée aux détails, aux matériaux et aux finitions.",
  },
];

const DEPARTEMENTS = ["Paris (75)", "Seine-et-Marne (77)", "Yvelines (78)", "Essonne (91)", "Hauts-de-Seine (92)", "Seine-Saint-Denis (93)", "Val-de-Marne (94)", "Val-d'Oise (95)"];

const AboutPage = () => {
  useSEO(
    "À propos — BÂTI 360 | Entreprise générale du bâtiment en Île-de-France",
    "BÂTI 360 accompagne particuliers, architectes, syndics et professionnels dans leurs projets de rénovation et de transformation à Paris et en Île-de-France."
  );

  return (
    <div className="pt-20 md:pt-24">
      <header className="border-b border-black/10 bg-white py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <p className="mb-4 flex items-center gap-3 text-[12px] font-bold uppercase tracking-[0.22em] text-bati-sky">
              <span className="h-px w-10 bg-bati-sky" /> Qui sommes-nous
            </p>
            <h1 className="font-display text-4xl font-extrabold tracking-tight text-bati-ink sm:text-5xl">
              Construire avec méthode.<br />Rénover avec exigence.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-bati-ink/60">
              BÂTI 360 accompagne ses clients dans leurs projets de rénovation, de transformation et d'amélioration du bâtiment. Basée en Seine-et-Marne, l'entreprise intervient principalement à Paris et dans toute l'Île-de-France, pour des particuliers, des architectes, des syndics, des investisseurs et des professionnels.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border border-black/10 bg-white p-8">
              <img src="/logo-full.webp" alt="BÂTI 360" loading="lazy" className="mx-auto max-h-72 w-auto" />
            </div>
          </Reveal>
        </div>
      </header>

      <section className="bg-white py-20 lg:py-28" data-testid="about-principles">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="mb-14">
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-bati-ink sm:text-4xl">
              Notre approche repose sur trois principes
            </h2>
          </Reveal>
          <div className="grid gap-px bg-black/10 md:grid-cols-3">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.08} className="bg-white">
                <div className="flex h-full flex-col p-8 lg:p-10">
                  <span className="font-display text-6xl font-extrabold tracking-tight text-bati-sky/90">{p.num}</span>
                  <h3 className="mt-5 font-display text-2xl font-bold text-bati-ink">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-bati-ink/60">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-bati-mist py-20 lg:py-28" data-testid="about-zone">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <div className="overflow-hidden">
              <img src={IMAGES.blueprints} alt="Étude technique d'un projet de rénovation" loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-[1.04]" />
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mb-4 flex items-center gap-3 text-[12px] font-bold uppercase tracking-[0.22em] text-bati-sky">
              <span className="h-px w-10 bg-bati-sky" /> Zone d'intervention
            </p>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-bati-ink sm:text-4xl">Paris & Île-de-France</h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-bati-ink/60">
              De la rénovation d'appartement haussmannien à Paris à la rénovation de maison en Île-de-France, notre équipe se déplace sur l'ensemble de la région pour étudier votre projet.
            </p>
            <div className="mt-7 flex flex-wrap gap-2">
              {DEPARTEMENTS.map((d) => (
                <span key={d} className="border border-black/10 bg-white px-4 py-2 text-xs font-semibold text-bati-ink/70">
                  {d}
                </span>
              ))}
            </div>
            <p className="mt-7 text-sm text-bati-ink/50">
              Siège : {COMPANY.address}, {COMPANY.city}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-bati-blue py-20 text-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-5 lg:px-8">
          <h2 className="max-w-xl font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
            Votre projet mérite une étude sérieuse.
          </h2>
          <Link
            to="/contact"
            data-testid="about-cta-devis"
            className="bg-white px-8 py-4 text-[13px] font-bold uppercase tracking-[0.12em] text-bati-blue transition-colors duration-300 hover:bg-bati-sky hover:text-white"
          >
            Demander un devis
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
